/**
 * courseMutationsApi.ts
 * RTK Query mutations for course CRUD operations.
 * Replaces direct supabase.rpc() calls in:
 *  - UnifiedCourseCard.tsx  (slot upsert, slot removal)
 *  - useCourseDataSupabase.ts (create, update, delete dry-run, delete apply)
 *
 * Uses pessimistic onQueryStarted cache updates per workflow standard.
 */
import { rootApi } from "../../../../lib/api/rootApi";

// ---------------------------------------------------------------------------
// Request / Response types
// ---------------------------------------------------------------------------

export interface CourseCreateRequest {
  name: string;
  subject_id?: string;
  max_students: number;
  year_groups: number[];
  description?: string;
  special_info?: string;
  team_info?: string;
  emoticon?: string;
  allow_parent_registration?: boolean;
  description_visible_to_parents?: boolean;
  start_date?: string;
  end_date?: string;
  status?: string;
}

export interface SlotPayload {
  schedule_id?: string;
  weekday: string;
  lesson: number | null;
  room: string | null;
  teacher_ids: string[];
  primary_teacher_id: string | null;
  start_date: string;
  end_date: string;
  semester_id?: string | null;
}

export interface DeleteDryRunResult {
  hard_delete_possible?: boolean;
  to_end?: number;
  active_schedules?: number;
  future_no_deps?: number;
  future_with_deps?: number;
  draft_items?: number;
}

/** Standard success/failure envelope returned by course RPCs */
export interface CourseRpcResult {
  success: boolean;
  message?: string;
  course_id?: string;
}

// ---------------------------------------------------------------------------
// Injected endpoints
// ---------------------------------------------------------------------------

export const courseMutationsApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── Create course (+ optional slots + future lessons) ─────────
    createCourse: builder.mutation<CourseRpcResult, {
      p_user_profile_id: string;
      p_name: string;
      p_subject_id?: string;
      p_max_students?: number;
      p_year_groups?: number[];
      p_description?: string;
      p_team_info?: string;
      p_emoticon?: string;
      p_allow_parent_registration?: boolean;
      p_slots?: SlotPayload[];
    }>({
      query: (params) => ({
        type: "rpc",
        rpc: "fn_course_management_create_course",
        params,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(rootApi.util.invalidateTags(["Courses", "Timetable"]));
        } catch {
          // Mutation failed — no cache action needed
        }
      },
    }),

    // ── Update course ─────────────────────────────────────────────
    updateCourse: builder.mutation<CourseRpcResult, {
      p_course_id: string;
      p_updates: Partial<CourseCreateRequest>;
    }>({
      query: (params) => ({
        type: "rpc",
        rpc: "fn_course_management_update_course",
        params,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(rootApi.util.invalidateTags(["Courses"]));
        } catch { /* noop */ }
      },
    }),

    // ── Delete dry-run (preview) ──────────────────────────────────
    deleteDryRun: builder.mutation<DeleteDryRunResult, {
      p_user_profile_id: string;
      p_course_id: string;
    }>({
      query: (params) => ({
        type: "rpc",
        rpc: "fn_course_management_delete_course_dry_run",
        params,
      }),
    }),

    // ── Delete apply ──────────────────────────────────────────────
    deleteApply: builder.mutation<CourseRpcResult, {
      p_user_profile_id: string;
      p_course_id: string;
      p_remove_draft_items?: boolean;
    }>({
      query: (params) => ({
        type: "rpc",
        rpc: "fn_course_management_delete_course_apply",
        params,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(rootApi.util.invalidateTags(["Courses", "Enrollments", "Timetable"]));
        } catch { /* noop */ }
      },
    }),

    // ── Upsert schedule slots + future lessons ────────────────────
    upsertSlotsAndFutureLessons: builder.mutation<CourseRpcResult, {
      p_course_id: string;
      p_user_profile_id: string;
      p_slots: SlotPayload[];
    }>({
      query: (params) => ({
        type: "rpc",
        rpc: "fn_course_management_upsert_slots_and_future_lessons",
        params,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(rootApi.util.invalidateTags(["Courses", "Timetable"]));
        } catch { /* noop */ }
      },
    }),

    // ── Remove all slots → unplanned ─────────────────────────────
    removeSlotsToUnplanned: builder.mutation<CourseRpcResult, {
      p_user_profile_id: string;
      p_course_id: string;
    }>({
      query: (params) => ({
        type: "rpc",
        rpc: "fn_course_management_remove_slots_to_unplanned",
        params,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(rootApi.util.invalidateTags(["Courses", "Timetable"]));
        } catch { /* noop */ }
      },
    }),

    // ── Get template data for import ─────────────────────────────
    getCourseTemplateData: builder.query<any, void>({
      query: () => ({
        type: "rpc",
        rpc: "get_course_template_data_react",
        params: {},
      }),
      providesTags: ["Courses", "Settings"],
    }),

    /**
     * Fetch teacher occupancy per day+period.
     * Returns map: "day_id::block_number" -> [{ teacher_id, course_name }]
     */
    getTeacherOccupancy: builder.query<
      Record<string, { teacher_id: string; course_name: string }[]>,
      { excludeCourseId?: string }
    >({
      query: (params) => ({
        type: "rpc",
        rpc: "fn_course_management_teacher_occupancy",
        params: { p_exclude_course_id: params.excludeCourseId || null },
      }),
      providesTags: ["Courses"],
    }),
  }),
});

export const {
  useCreateCourseMutation,
  useUpdateCourseMutation,
  useDeleteDryRunMutation,
  useDeleteApplyMutation,
  useUpsertSlotsAndFutureLessonsMutation,
  useRemoveSlotsToUnplannedMutation,
  useLazyGetCourseTemplateDataQuery,
  useGetTeacherOccupancyQuery,
} = courseMutationsApi;
